import { __decorate } from "tslib";
import { LitElement, html, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { elements } from './sdb-elements.js';
/**
 * Button element
 *
 * @prop {String} label - mandatory, used to define the button text

 * @prop {String} icon - optional, used to set an icon at the beginning of the button
 * @prop {String} iconSize - optional, used to set the size of the icon (default 20)
 * @prop {String} padding - optional, used to set the padding of the button (default 10px 15px)
 *
 * @prop {Boolean} secondary - optional, dislpays the button in the secondary color
 * @prop {Boolean} themecolor - optional, displays the button in the active themecolor
 * @prop {Boolean} outlined - optional, used to display a bordered button (no fill)
 * @prop {Boolean} media - optional, lightgrey button with icon in primary themecolor
 * @prop {Boolean} light - optional, displays text in text-light color
 * @prop {Boolean} disabled - optional, disables the button (also removes all pointer events)
 * @prop {Boolean} locked - optional, overlays the button with a lock layer (also removes pointer events)
 * @prop {Boolean} iconOnly - optional, used for buttons that only show an icon (removes margin)
 *
 */
let SDBContentButton = class SDBContentButton extends LitElement {
    constructor() {
        super(...arguments);
        this.icon = '';
        this.iconSize = '20';
        this.iconOnly = false;
        this.label = '';
        this.padding = '10px 15px';
        this.secondary = false;
        this.themecolor = false;
        this.media = false;
        this.disabled = false;
        this.locked = false;
        this.outlined = false;
        this.light = false;
    }
    render() {
        return html `
            <button style="padding:${this.padding}">
                ${this.locked
            ? html `<div class="lockedOverlay">
                          <sdb-content-icon
                              size="24"
                              icon="fal fa-lock"
                          ></sdb-content-icon>
                      </div>`
            : ``}
                ${this.icon
            ? html `<sdb-content-icon
                          size="${this.iconSize}"
                          .icon=${this.icon}
                      ></sdb-content-icon>`
            : ``}
                ${this.label}
            </button>
        `;
    }
};
SDBContentButton.styles = [
    elements,
    css `
            :host button {
                position: relative;
                display: inline-flex;
                align-items: center;
                margin: 5px 5px 5px 0;
                background: var(--btn-primary);
                color: var(--btn-primary-text);
                text-transform: uppercase;
                border: none;
                border-radius: 2px;
                cursor: pointer;
                font: var(--font-default);
                transition: scale 0.1s ease-in-out;
                -webkit-transform: rotateZ(360deg);
            }

            :host button:focus {
                outline: none !important;
            }

            :host button:hover {
                transform: var(--sdb-button-scale-factor, scale(1.05));
            }

            :host button sdb-content-icon {
                margin-right: 10px;
            }

            :host([locked]) button sdb-content-icon {
                margin: 0;
            }

            :host([locked]) .lockedOverlay {
                display: flex;
                position: absolute;
                background: RGBA(255, 255, 255, 0.75);
                width: 100%;
                height: 100%;
                top: 0;
                left: 0;
                align-items: center;
                justify-content: center;
                color: var(--text-primary);
            }

            :host([iconOnly]) button sdb-content-icon {
                margin-right: 0;
            }

            :host([themecolor]) button {
                background: var(--theme-primary);
                color: var(--theme-primary-text);
            }

            :host([secondary]) button {
                background: var(--btn-secondary);
                color: var(--btn-secondary-text);
            }

            :host([media]) button {
                background: var(--element-lightgrey);
                color: var(--text-primary);
            }

            :host([media]) button sdb-content-icon {
                color: var(--theme-primary);
            }

            :host([disabled]),
            :host([locked]) {
                pointer-events: none;
            }

            :host([outlined]) button {
                background: transparent;
                border: 1px solid var(--element-grey);
                color: var(--text-primary);
            }

            :host([outlined]) button:hover {
                background: rgba(0, 0, 0, 0.1);
            }

            :host([light]) button {
                border-color: white;
                color: var(--text-light);
            }

            :host([light]) button:hover {
                color: var(--text-light);
            }

            :host([disabled]) button {
                color: var(--element-disabled);
                background: var(--btn-disabled);
            }
        `,
];
__decorate([
    property({ type: String })
], SDBContentButton.prototype, "icon", void 0);
__decorate([
    property({ type: String })
], SDBContentButton.prototype, "iconSize", void 0);
__decorate([
    property({ type: Boolean })
], SDBContentButton.prototype, "iconOnly", void 0);
__decorate([
    property({ type: String })
], SDBContentButton.prototype, "label", void 0);
__decorate([
    property({ type: String })
], SDBContentButton.prototype, "padding", void 0);
__decorate([
    property({ type: Boolean })
], SDBContentButton.prototype, "secondary", void 0);
__decorate([
    property({ type: Boolean })
], SDBContentButton.prototype, "themecolor", void 0);
__decorate([
    property({ type: Boolean })
], SDBContentButton.prototype, "media", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], SDBContentButton.prototype, "disabled", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], SDBContentButton.prototype, "locked", void 0);
__decorate([
    property({ type: Boolean })
], SDBContentButton.prototype, "outlined", void 0);
__decorate([
    property({ type: Boolean })
], SDBContentButton.prototype, "light", void 0);
SDBContentButton = __decorate([
    customElement('sdb-content-button')
], SDBContentButton);
export { SDBContentButton };
//# sourceMappingURL=sdb-content-button.js.map